import { useRefundCashDrawer } from '@/lib/services/cash-drawers/useRefundCashDrawer'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { CheckCircle2, PackageCheck } from 'lucide-react'
import {
  ORDER_ASIDE_RAISED_SURFACE_CLASS,
  ORDER_ASIDE_SUBTLE_SURFACE_CLASS,
} from '@/components/orders/order-aside.styles'
import { OrderAsidePanelCard } from '@/components/orders/OrderAsidePanelCard'
import { CustomerNotificationSwitch } from '@/components/orders/CustomerNotificationSwitch'
import { getProductLineItemGroups } from '@/components/orders/order-product-line-groups'
import { ActionSurface } from '@/components/patterns/action-surface'
import { ActionRow } from '@/components/patterns/action-row'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { CurrencyInput } from '@/components/ui/currency-input'
import { Input } from '@/components/ui/input'
import { CardContent, CardHeader, CardTitle } from '../../ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { Typography } from '@/components/ui/typography'
import { RefundSettlementStatus } from '@/lib/enums/RefundSettlementStatus'
import { ReturnFulfillmentStatus } from '@/lib/enums/ReturnFulfillmentStatus'
import { useAbility } from '@/lib/casl'
import queryClient from '@/lib/queryClient'
import {
  getCashDrawerOptionLabel,
  manualTenderLocationId,
} from '@/lib/services/cash-drawers/cash-drawers.utils'
import {
  useRefundRequestStatus,
  salesOrderQueryKeys,
  useRefundFullSalesOrderMutation,
  useRefundPartialSalesOrderMutation,
  useRefundQuoteMutation,
} from '@/lib/services/sales-orders/sales-orders.query'
import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import {
  outcomeReasonRequiresNote,
  salesOrderOutcomeReasonOptions,
} from '@/lib/sales-order-outcome-reasons'
import {
  cn,
  formatCurrency,
  generateIdempotencyKey,
  getErrorMessage,
  isRecord,
  normalizeCurrencyValue as roundToCents,
  parseCurrency,
  readProperty,
} from '@/lib/utils'
import type {
  RefundQuote,
  RefundQuoteMode,
  RefundQuoteRow,
  RefundRequestStatusSnapshot,
  RefundTotals,
} from '@/lib/services/refunds/refunds.types'
import type {
  RefundLineItemSelection,
  RefundRestockLineItem,
  SalesOrder,
  SalesOrderOutcomeReasonInput,
} from '@/lib/services/sales-orders/sales-orders.types'
import { CustomerHandoffDialog } from './CustomerHandoffDialog'
import { getRefundDestinationPreviewLines, getRefundDestinationTotals } from './refund-destinations'
import { RefundDestinationList } from './RefundDestinationList'
import { getLoadedSettlementStatus } from './refund-settlement-status'
import { RefundSettlementDetails } from './RefundRequestProgress'
import { CashDrawerStatusError } from '@/components/pos/CashDrawerStatusError'
import {
  CASH_DRAWER_STATUS_UNAVAILABLE_MESSAGE,
  cashDrawerQueriesAreFetching,
  cashDrawerQueriesHaveError,
  refreshCashDrawerSession,
  retryFailedCashDrawerQueries,
} from '@/lib/services/cash-drawers/cash-drawers.preflight'

const QUOTE_DEBOUNCE_MS = 250

/** Typed refund amounts by quote row id, as the inputs hold them. */
type DraftAmounts = Record<number, string>

/** Whole units to restock by quote row id. */
type RestockQuantities = Record<number, number>

type RestockQuantityChangeHandler = (row: RefundQuoteRow, value: number | string) => void

interface RefundQuoteRowGroup {
  key: string
  salesOrderItemId: number | null
  rootRows: RefundQuoteRow[]
  childRows: RefundQuoteRow[]
}

function formatMoneyInputValue(amount: number): string {
  return roundToCents(Math.max(Number(amount ?? 0), 0)).toFixed(2)
}

function buildSelectedRowIds(rows: RefundQuoteRow[] = []): number[] {
  return rows.filter((row) => Number(row?.selected_amount ?? 0) > 0).map((row) => Number(row.id))
}

function buildDraftAmounts(rows: RefundQuoteRow[] = []): DraftAmounts {
  return Object.fromEntries(
    rows
      .filter((row) => Number(row?.selected_amount ?? 0) > 0)
      .map((row) => [Number(row.id), formatMoneyInputValue(row.selected_amount)])
  )
}

/** The API's message on a failed request, when it sent one. */
function errorResponseMessage(error: unknown): string | undefined {
  const message = readProperty(readProperty(readProperty(error, 'response'), 'data'), 'message')

  return typeof message === 'string' ? message : undefined
}

/**
 * A stale-quote refusal carries the current quote, built by the same service as `refund-quote`
 * (`SalesOrderController::staleQuoteResponse()`).
 */
function isRefundQuote(value: unknown): value is RefundQuote {
  return (
    isRecord(value) &&
    Array.isArray(value.rows) &&
    Array.isArray(value.selected_line_items) &&
    Array.isArray(value.warnings) &&
    isRecord(value.summary) &&
    isRecord(value.refund_plan)
  )
}

function rowHelperText(row: RefundQuoteRow): string {
  const lines: string[] = []

  if (row?.locked_reason) {
    lines.push(row.locked_reason)
  }

  if (Number(row?.amount_refunded ?? 0) > 0) {
    lines.push(
      Number(row?.max_amount ?? 0) <= 0
        ? `Already refunded ${formatCurrency(row?.amount_refunded ?? 0)}.`
        : `Already refunded ${formatCurrency(row?.amount_refunded ?? 0)}. Up to ${formatCurrency(row?.max_amount ?? 0)} left to refund.`
    )
  }

  if (Number(row?.amount_reserved ?? 0) > 0) {
    lines.push(`Refund in progress: ${formatCurrency(row?.amount_reserved ?? 0)}`)
  }

  if ((row?.type ?? '') === 'product' && Number(row?.quantity_remaining ?? 0) > 1) {
    lines.unshift(`${row.quantity_remaining} remaining`)
  }

  return lines.join(' ')
}

function refundRowDiscountLabel(row: RefundQuoteRow, isSelected: boolean): string {
  const selectedAmount = roundToCents(row?.selected_amount ?? 0)
  const selectedDiscountAmount = roundToCents(row?.selected_discount_amount ?? 0)
  const remainingDiscountAmount = roundToCents(row?.discount_remaining ?? 0)
  const discountAmount = isSelected
    ? selectedDiscountAmount > 0 || selectedAmount > 0
      ? selectedDiscountAmount
      : remainingDiscountAmount
    : remainingDiscountAmount

  return discountAmount > 0 ? `After ${formatCurrency(discountAmount)} discount` : ''
}

function summaryRows(
  summary: Partial<RefundTotals> = {},
  shopFeeLabel: string
): Array<[string, number]> {
  const rows: Array<[string, number]> = [
    ['Subtotal', parseCurrency(summary?.subtotal)],
    ['Delivery', parseCurrency(summary?.freight)],
    ['Tip', parseCurrency(summary?.tip)],
    ['Relay Fee', parseCurrency(summary?.relay_fee)],
    ['Service Fee', parseCurrency(summary?.variable_service_fee)],
    [shopFeeLabel, parseCurrency(summary?.florist_fee)],
    ['Tax', parseCurrency(summary?.tax)],
    ['Total', parseCurrency(summary?.total)],
  ]

  return rows.filter(
    ([label, amount]) => label === 'Subtotal' || label === 'Tax' || label === 'Total' || amount > 0
  )
}

function quoteRequiresDraftSync(nextQuote: RefundQuote): boolean {
  return (nextQuote?.warnings ?? []).some((warning) =>
    ['row_missing', 'row_amount_clamped', 'full_refund_rollup_capped'].includes(
      String(warning?.code ?? '')
    )
  )
}

function normalizeSelectedLineItems(
  lineItems: RefundLineItemSelection[] = []
): RefundLineItemSelection[] {
  return [...lineItems]
    .map((lineItem) => ({
      id: Number(lineItem?.id ?? 0),
      amount: roundToCents(lineItem?.amount ?? 0),
    }))
    .filter((lineItem) => Number.isInteger(lineItem.id) && lineItem.id !== 0 && lineItem.amount > 0)
    .sort((left, right) => left.id - right.id)
}

function selectedLineItemsMatch(
  left: RefundLineItemSelection[],
  right: RefundLineItemSelection[]
): boolean {
  const normalizedLeft = normalizeSelectedLineItems(left)
  const normalizedRight = normalizeSelectedLineItems(right)

  if (normalizedLeft.length !== normalizedRight.length) {
    return false
  }

  return normalizedLeft.every(
    (lineItem, index) =>
      lineItem.id === normalizedRight[index]?.id &&
      lineItem.amount === normalizedRight[index]?.amount
  )
}

// The quote says how many whole units this refund may put back in stock; the API holds the refund to it.
function maxRestockQuantityForRow(row: RefundQuoteRow): number {
  return row.type === 'product' ? row.restockable_quantity : 0
}

// Quote rows carry their flat sales order item ids, which is all the grouping reads.
function groupRefundQuoteRows(rows: RefundQuoteRow[] = []): RefundQuoteRowGroup[] {
  return getProductLineItemGroups(rows).map((group, index) => ({
    key: group.salesOrderItemId ? group.key : `row:${group.rootItems[0]?.id ?? index}`,
    salesOrderItemId: group.salesOrderItemId,
    rootRows: group.rootItems,
    childRows: group.childItems,
  }))
}

function getQuoteRowsForSelectionToggle(
  row: RefundQuoteRow,
  quoteRowGroups: RefundQuoteRowGroup[] = [],
  {
    isSelecting = true,
    selectedRowIds = [],
  }: { isSelecting?: boolean; selectedRowIds?: number[] } = {}
): RefundQuoteRow[] {
  const rowId = Number(row?.id)
  const parentGroup = quoteRowGroups.find((group) =>
    group.rootRows.some((rootRow) => Number(rootRow?.id) === rowId)
  )

  if (!parentGroup) {
    return [row]
  }

  const selectedRowIdSet = new Set(selectedRowIds.map(Number))
  const selectedChildRows = parentGroup.childRows.filter((childRow) =>
    selectedRowIdSet.has(Number(childRow?.id))
  )
  const hasOtherSelectedRootRow = parentGroup.rootRows.some(
    (rootRow) => Number(rootRow?.id) !== rowId && selectedRowIdSet.has(Number(rootRow?.id))
  )

  if (isSelecting) {
    return hasOtherSelectedRootRow ? [row, ...selectedChildRows] : [row, ...parentGroup.childRows]
  }

  if (hasOtherSelectedRootRow) {
    return [row]
  }

  return [row, ...parentGroup.childRows]
}

function buildRestockLineItemsPayload(
  rows: RefundQuoteRow[] = [],
  restockQuantitiesByRowId: RestockQuantities = {}
): RefundRestockLineItem[] {
  return rows
    .map((row) => {
      const maxQuantity = maxRestockQuantityForRow(row)
      const requestedQuantity = Math.min(
        Math.max(Math.floor(Number(restockQuantitiesByRowId[Number(row.id)] ?? 0)), 0),
        maxQuantity
      )

      return requestedQuantity > 0
        ? {
            id: Number(row.id),
            quantity: requestedQuantity,
          }
        : null
    })
    .filter((lineItem) => lineItem !== null)
}

function restockCountFromLineItems(restockLineItems: RefundRestockLineItem[] = []): number {
  return restockLineItems.reduce((total, lineItem) => total + Number(lineItem?.quantity ?? 0), 0)
}

interface RestockQuantityControlProps {
  row: RefundQuoteRow
  restockQuantity?: number
  maxRestockQuantity?: number
  onRestockQuantityChange: RestockQuantityChangeHandler
}

function RestockQuantityControl({
  row,
  restockQuantity = 0,
  maxRestockQuantity = 0,
  onRestockQuantityChange,
}: RestockQuantityControlProps) {
  if (maxRestockQuantity <= 0) {
    return null
  }

  return (
    <div className="flex flex-col items-end gap-1 pt-1">
      <Typography as="label" variant="caption" className="inline-flex items-center gap-2">
        <Checkbox
          checked={restockQuantity > 0}
          onCheckedChange={(checked) =>
            onRestockQuantityChange(row, checked === true ? maxRestockQuantity : 0)
          }
          aria-label={`Restock ${row.label}`}
        />
        <span>Restock inventory</span>
      </Typography>
      {restockQuantity > 0 && maxRestockQuantity > 1 && (
        <Input
          type="number"
          size="xs"
          min="1"
          max={maxRestockQuantity}
          value={restockQuantity}
          aria-label={`Restock quantity for ${row.label}`}
          onChange={(event) => onRestockQuantityChange(row, event.target.value)}
          className="w-16 text-right"
        />
      )}
    </div>
  )
}

function getTerminalRefundToast(
  status: RefundRequestStatusSnapshot | null | undefined
): { title: string; description: string } | null {
  const settlementStatus = status?.settlement_status

  if (settlementStatus === RefundSettlementStatus.Failed.Value) {
    return {
      title: 'Refund failed',
      description:
        'No money was refunded. Review the settlement details below before trying again.',
    }
  }

  if (settlementStatus === RefundSettlementStatus.PartiallySettled.Value) {
    return {
      title: 'Refund only partially completed',
      description: `Settled ${formatCurrency(status?.settled_total ?? 0)} · Failed ${formatCurrency(
        status?.failed_total ?? 0
      )}. Review the settlement details below before trying again.`,
    }
  }

  return null
}

interface QuoteRowProps {
  row: RefundQuoteRow
  isSelected: boolean
  draftAmount: string
  restockQuantity?: number
  maxRestockQuantity?: number
  relationship?: 'root' | 'child'
  onToggle: (row: RefundQuoteRow, checked: boolean) => void
  onAmountChange: (row: RefundQuoteRow, value: string) => void
  onRestockQuantityChange: RestockQuantityChangeHandler
}

function QuoteRow({
  row,
  isSelected,
  draftAmount,
  restockQuantity = 0,
  maxRestockQuantity = 0,
  relationship = 'root',
  onToggle,
  onAmountChange,
  onRestockQuantityChange,
}: QuoteRowProps) {
  const isLocked = row?.is_locked === true
  const toggleRowSelection = () => onToggle(row, !isSelected)
  const hasRestockControl = isSelected && maxRestockQuantity > 0
  const isChild = relationship === 'child'
  const helperText = [refundRowDiscountLabel(row, isSelected), rowHelperText(row)]
    .filter(Boolean)
    .join(' ')

  return (
    <li
      className={cn('relative', isChild && 'ml-5 pl-5 sm:ml-7 sm:pl-6')}
      data-id={row.id}
      data-relationship={relationship}
    >
      {isChild ? (
        <>
          <span aria-hidden="true" className="absolute left-0 top-0 h-8 w-px bg-border/70" />
          <span aria-hidden="true" className="absolute left-0 top-8 h-px w-4 bg-border/70" />
        </>
      ) : null}
      <div
        className={cn(
          'flex items-start justify-between gap-3 rounded-lg border px-3 py-3 transition-colors',
          isSelected ? 'border-primary/35 bg-primary/5' : 'border-border/60 bg-background/70',
          isChild && 'bg-background/60'
        )}
      >
        <div className="flex min-w-0 items-start gap-3">
          <Checkbox
            checked={isSelected}
            disabled={isLocked}
            className="mt-0.5"
            onCheckedChange={(checked) => onToggle(row, checked === true)}
            aria-label={`Select ${row.label}`}
          />
          <ActionRow
            padding="none"
            radius="none"
            className="min-w-0 flex-col items-start"
            disabled={isLocked}
            onClick={toggleRowSelection}
          >
            <Typography variant="label" tone="inherit" className="max-w-full truncate">
              {row.label}
            </Typography>
            {helperText && (
              <Typography variant="caption" className="mt-0.5">
                {helperText}
              </Typography>
            )}
          </ActionRow>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          {isSelected && !isLocked ? (
            <CurrencyInput
              size="sm"
              align="right"
              aria-label={`Refund amount for ${row.label}`}
              value={draftAmount ?? ''}
              onValueChange={(value) => onAmountChange(row, value)}
              onBlurValueChange={(value) => onAmountChange(row, value)}
              wrapperClassName="w-[96px]"
            />
          ) : (
            <Typography variant="bodySmall">{formatCurrency(row.max_amount ?? 0)}</Typography>
          )}
          {isSelected && (
            <Typography variant="caption">Max {formatCurrency(row.max_amount ?? 0)}</Typography>
          )}
          {hasRestockControl && (
            <RestockQuantityControl
              row={row}
              restockQuantity={restockQuantity}
              maxRestockQuantity={maxRestockQuantity}
              onRestockQuantityChange={onRestockQuantityChange}
            />
          )}
        </div>
      </div>
    </li>
  )
}

interface FullRefundRestockRowsProps {
  rows: RefundQuoteRow[]
  restockQuantitiesByRowId: RestockQuantities
  onRestockQuantityChange: RestockQuantityChangeHandler
}

function FullRefundRestockRows({
  rows,
  restockQuantitiesByRowId,
  onRestockQuantityChange,
}: FullRefundRestockRowsProps) {
  const restockableRows = rows.filter((row) => maxRestockQuantityForRow(row) > 0)

  if (restockableRows.length === 0) {
    return null
  }

  return (
    <div className="grid gap-2">
      <div className="grid gap-1">
        <Typography variant="label">Inventory</Typography>
        <Typography variant="caption">
          Choose which returned tracked items should be added back to inventory after the refund
          settles.
        </Typography>
      </div>
      <ul className="space-y-2">
        {restockableRows.map((row) => {
          const maxRestockQuantity = maxRestockQuantityForRow(row)

          return (
            <li
              key={row.id}
              className={cn(
                ORDER_ASIDE_SUBTLE_SURFACE_CLASS,
                'grid grid-cols-[minmax(0,1fr)_auto] gap-3 px-3 py-2'
              )}
            >
              <div className="min-w-0">
                <Typography variant="label" className="block max-w-full truncate">
                  {row.label}
                </Typography>
                <Typography variant="caption" className="block">
                  {`${maxRestockQuantity} whole item${maxRestockQuantity === 1 ? '' : 's'} available to restock`}
                </Typography>
              </div>
              <RestockQuantityControl
                row={row}
                restockQuantity={Math.min(
                  Number(restockQuantitiesByRowId[Number(row.id)] ?? 0),
                  maxRestockQuantity
                )}
                maxRestockQuantity={maxRestockQuantity}
                onRestockQuantityChange={onRestockQuantityChange}
              />
            </li>
          )
        })}
      </ul>
    </div>
  )
}

interface PaidSalesOrderRefundFormProps {
  order: SalesOrder
  onCompleted?: () => void
}

function PaidSalesOrderRefundForm({ order, onCompleted }: PaidSalesOrderRefundFormProps) {
  const orgId = useRouteOrganizationId()
  const ability = useAbility()
  const canOperateCashDrawers = ability?.can('operate', 'cash-drawers') === true
  const targetLocationId = manualTenderLocationId(order)
  const {
    drawer: {
      statusQuery: cashDrawerStatusQuery,
      trackingEnabled: cashDrawerTrackingEnabled,
      workstationsQuery,
      pairedWorkstation,
      currentDrawerQuery,
      resolvedDrawer: resolvedCashDrawer,
      isLoading: cashDrawerQueriesLoading,
      pairingBlocker,
    },
    resolve: resolveRefundCashDrawer,
  } = useRefundCashDrawer({
    organizationId: orgId,
    locationId: targetLocationId,
    canOperateCashDrawers,
    enabled: true,
  })
  const { mutateAsync: quoteRefund } = useRefundQuoteMutation()
  const { mutateAsync: refundPartialSalesOrder } = useRefundPartialSalesOrderMutation()
  const { mutateAsync: refundFullSalesOrder } = useRefundFullSalesOrderMutation()
  const loadedSettlementStatus = getLoadedSettlementStatus(order)
  const [submittedRefundRequestId, setSubmittedRefundRequestId] = useState<number | null>(null)
  const refundRequestId = submittedRefundRequestId ?? loadedSettlementStatus?.refund_request_id
  const settlementQuery = useRefundRequestStatus({
    refundRequestId,
    salesOrderId: order?.id,
    initialStatus:
      Number(loadedSettlementStatus?.refund_request_id) === Number(refundRequestId)
        ? loadedSettlementStatus
        : undefined,
  })
  const settlementStatus = settlementQuery.data
  const [mode, setMode] = useState<RefundQuoteMode | null>(null)
  const [selectedRowIds, setSelectedRowIds] = useState<number[]>([])
  const [draftAmounts, setDraftAmounts] = useState<DraftAmounts>({})
  const [quote, setQuote] = useState<RefundQuote | null>(null)
  const [isQuoteLoading, setIsQuoteLoading] = useState(false)
  const [reason, setReason] = useState<SalesOrderOutcomeReasonInput | ''>('')
  const [reasonNote, setReasonNote] = useState('')
  const [sendCustomerEmail, setSendCustomerEmail] = useState(true)
  const returnFulfillmentStatus = ReturnFulfillmentStatus.NoItemsToReturn.Value
  const [restockQuantitiesByRowId, setRestockQuantitiesByRowId] = useState<RestockQuantities>({})
  const [isRefundConfirmOpen, setIsRefundConfirmOpen] = useState(false)
  const [isSubmittingRefund, setIsSubmittingRefund] = useState(false)
  const pendingRefundIdempotencyKeyRef = useRef<string | null>(null)
  const quoteRequestSequenceRef = useRef(0)
  const quoteDebounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const buildQuoteRequestLineItems = useCallback(
    (rowIds: number[] = selectedRowIds, amounts: DraftAmounts = draftAmounts) =>
      rowIds
        .map((rowId) => ({
          id: Number(rowId),
          amount: roundToCents(parseCurrency(amounts[rowId] ?? 0)),
        }))
        .filter((row) => row.amount > 0),
    [draftAmounts, selectedRowIds]
  )

  const quoteRows = useMemo(() => quote?.rows ?? [], [quote])
  const quoteRowGroups = useMemo(() => groupRefundQuoteRows(quoteRows), [quoteRows])
  const quoteRowsById = useMemo(
    () => new Map(quoteRows.map((row) => [Number(row.id), row])),
    [quoteRows]
  )
  const draftQuoteLineItems = useMemo(
    () => buildQuoteRequestLineItems(),
    [buildQuoteRequestLineItems]
  )
  const selectedRows = selectedRowIds
    .map((rowId) => quoteRowsById.get(Number(rowId)))
    .filter((row) => row !== undefined)
  const selectedRestockLineItems = buildRestockLineItemsPayload(
    selectedRows,
    restockQuantitiesByRowId
  )
  const restockCount = restockCountFromLineItems(selectedRestockLineItems)
  const destinationPreviewLines = getRefundDestinationPreviewLines(quote?.refund_plan)
  const { cardDestinationTotal, manualDestinationTotal, hasManualDestination, shortfallTotal } =
    getRefundDestinationTotals(quote?.refund_plan)
  const cashDrawerStatusUnavailable = cashDrawerQueriesHaveError(
    cashDrawerStatusQuery,
    workstationsQuery,
    currentDrawerQuery
  )
  const isCashDrawerLoading = hasManualDestination && cashDrawerQueriesLoading
  const hasRefundShortfall = shortfallTotal > 0
  // The server adds the locked fee on a final refund; only editable rows must match the draft.
  const editableLineItems = (lineItems: RefundLineItemSelection[]) =>
    lineItems.filter((lineItem) => quoteRowsById.get(Number(lineItem.id))?.is_locked !== true)
  const hasQuoteDraftMismatch =
    mode === 'partial_line_item' &&
    Boolean(quote) &&
    !selectedLineItemsMatch(
      editableLineItems(quote?.selected_line_items ?? []),
      editableLineItems(draftQuoteLineItems)
    )
  const isSettlementBlockingRefund =
    Boolean(refundRequestId) &&
    (!settlementStatus ||
      settlementStatus.is_replaced ||
      settlementStatus.settlement_status === RefundSettlementStatus.PendingSettlement.Value)
  const refundTotal = roundToCents(parseCurrency(quote?.summary?.total))
  const isRefundActionDisabled =
    !mode ||
    refundTotal <= 0 ||
    isQuoteLoading ||
    isSubmittingRefund ||
    isSettlementBlockingRefund ||
    hasRefundShortfall ||
    hasQuoteDraftMismatch

  const nextQuoteRequestSequence = () => {
    quoteRequestSequenceRef.current += 1

    return quoteRequestSequenceRef.current
  }

  const clearPendingQuoteDebounce = () => {
    if (!quoteDebounceTimeoutRef.current) {
      return
    }

    clearTimeout(quoteDebounceTimeoutRef.current)
    quoteDebounceTimeoutRef.current = null
  }

  const resetDraftState = useCallback(() => {
    pendingRefundIdempotencyKeyRef.current = null
    clearPendingQuoteDebounce()
    nextQuoteRequestSequence()
    setMode(null)
    setSelectedRowIds([])
    setDraftAmounts({})
    setQuote(null)
    setReason('')
    setReasonNote('')
    setSendCustomerEmail(true)
    setRestockQuantitiesByRowId({})
    setIsRefundConfirmOpen(false)
  }, [])

  const invalidateOrderQuery = async () => {
    if (!order?.id) {
      return
    }

    await queryClient.invalidateQueries({
      queryKey: salesOrderQueryKeys.show({
        organizationId: orgId,
        salesOrderId: order.id,
      }),
    })
  }

  const applyServerQuote = (
    nextQuote: RefundQuote,
    { syncDraftState = false }: { syncDraftState?: boolean } = {}
  ) => {
    setQuote(nextQuote)

    if (syncDraftState) {
      setSelectedRowIds(buildSelectedRowIds(nextQuote?.rows ?? []))
      setDraftAmounts(buildDraftAmounts(nextQuote?.rows ?? []))
    }
  }

  const refreshQuote = useCallback(
    async ({
      nextMode = mode,
      syncDraftState = false,
      lineItems = null,
    }: {
      nextMode?: RefundQuoteMode | null
      syncDraftState?: boolean
      lineItems?: RefundLineItemSelection[] | null
    } = {}) => {
      if (!nextMode || !order?.id) {
        setQuote(null)
        setIsQuoteLoading(false)
        return
      }

      const requestSequence = nextQuoteRequestSequence()
      setIsQuoteLoading(true)

      try {
        const data = await quoteRefund({
          id: order.id,
          mode: nextMode,
          ...(nextMode === 'partial_line_item'
            ? { lineItems: lineItems ?? buildQuoteRequestLineItems() }
            : {}),
        })

        if (quoteRequestSequenceRef.current !== requestSequence) {
          return
        }

        applyServerQuote(data, {
          syncDraftState: syncDraftState || nextMode === 'full' || quoteRequiresDraftSync(data),
        })
      } catch (error) {
        if (quoteRequestSequenceRef.current !== requestSequence) {
          return
        }

        toast.error(errorResponseMessage(error) ?? 'Failed to load refund quote')
      } finally {
        if (quoteRequestSequenceRef.current === requestSequence) {
          setIsQuoteLoading(false)
        }
      }
    },
    [buildQuoteRequestLineItems, mode, order?.id, quoteRefund]
  )

  const scheduleQuoteRefresh = useCallback(
    (
      nextMode: RefundQuoteMode | null = mode,
      lineItems: RefundLineItemSelection[] | null = null
    ) => {
      clearPendingQuoteDebounce()
      quoteDebounceTimeoutRef.current = setTimeout(
        () => {
          quoteDebounceTimeoutRef.current = null
          void refreshQuote({ nextMode, lineItems })
        },
        nextMode === 'full' ? 0 : QUOTE_DEBOUNCE_MS
      )
    },
    [mode, refreshQuote]
  )

  const trackRefundStatus = (
    refundRequestId: number,
    status: RefundRequestStatusSnapshot | undefined
  ) => {
    setSubmittedRefundRequestId(refundRequestId)
    if (status) {
      queryClient.setQueryData(
        salesOrderQueryKeys.refundRequestStatus({ organizationId: orgId, refundRequestId }),
        status
      )
    }
  }

  const showRefundSuccessToast = () => toast.success('Refund completed')

  const prepareCashConfirmation = async () => {
    const cashDrawerSessionId = await resolveRefundCashDrawer()

    return cashDrawerSessionId === null ? {} : { cashDrawerSessionId }
  }

  const syncQuoteFromError = (error: unknown) => {
    const nextQuote = readProperty(readProperty(readProperty(error, 'response'), 'data'), 'quote')
    if (!isRefundQuote(nextQuote)) {
      return false
    }

    applyServerQuote(nextQuote, { syncDraftState: true })
    toast.error(
      errorResponseMessage(error) ?? 'This refund quote changed. Review the updated totals.'
    )

    return true
  }

  const submitRefund = async () => {
    if (outcomeReasonRequiresNote(reason) && String(reasonNote ?? '').trim() === '') {
      toast.error('Add a note for Other.')

      return
    }

    if (!quote) {
      toast.error('Refund quote is not ready.')

      return
    }

    if (hasQuoteDraftMismatch) {
      toast.error('Refresh the refund quote before submitting.')

      return
    }

    setIsSubmittingRefund(true)
    // The till the cash comes from; null when nothing goes back by hand or Cash Drawer tracking is off.
    let cashDrawerSessionId: number | null = null
    if (hasManualDestination) {
      try {
        cashDrawerSessionId = await resolveRefundCashDrawer()
      } catch (error) {
        const message = readProperty(error, 'message')
        toast.error(
          (typeof message === 'string' ? message : '') ||
            getErrorMessage(error) ||
            CASH_DRAWER_STATUS_UNAVAILABLE_MESSAGE
        )
        setIsSubmittingRefund(false)
        return
      }
    }

    try {
      const idempotencyKey = pendingRefundIdempotencyKeyRef.current ?? generateIdempotencyKey()
      pendingRefundIdempotencyKeyRef.current = idempotencyKey
      const reasonPayload = {
        ...(reason ? { reason } : {}),
        ...(outcomeReasonRequiresNote(reason) && String(reasonNote ?? '').trim()
          ? { reasonNote: String(reasonNote).trim() }
          : {}),
      }

      const payloadBase = {
        id: order.id,
        returnFulfillmentStatus,
        idempotencyKey,
        cashHandoffConfirmed: hasManualDestination,
        sendCustomerEmail,
        ...(cashDrawerSessionId === null ? {} : { cashDrawerSessionId }),
        ...(selectedRestockLineItems.length > 0
          ? { restockLineItems: selectedRestockLineItems }
          : {}),
        ...reasonPayload,
      }

      const response =
        mode === 'full'
          ? await refundFullSalesOrder({
              ...payloadBase,
              quoteMode: 'full',
              expectedTotal: roundToCents(quote?.summary?.total ?? 0),
            })
          : await refundPartialSalesOrder({
              ...payloadBase,
              quoteMode: 'partial_line_item',
              amount: roundToCents(quote?.summary?.total ?? 0),
              lineItems: quote?.selected_line_items ?? [],
            })

      if (response?.refund_request_id) {
        trackRefundStatus(response.refund_request_id, response.refund_status)
        // The shared status query follows replacements; acceptance need not wait for another GET.
        const status = response.refund_status?.is_replaced ? null : response.refund_status
        const terminalToast = getTerminalRefundToast(status)
        if (terminalToast) {
          toast.error(terminalToast.title, { description: terminalToast.description })
          pendingRefundIdempotencyKeyRef.current = null
          setIsRefundConfirmOpen(false)
          await invalidateOrderQuery()
          return
        }
        if (status?.settlement_status === RefundSettlementStatus.Settled.Value) {
          showRefundSuccessToast()
        } else {
          toast.success('Refund submitted')
        }
      } else {
        showRefundSuccessToast()
      }
      await invalidateOrderQuery()
      resetDraftState()
      onCompleted?.()
    } catch (error) {
      if (syncQuoteFromError(error)) {
        return
      }

      toast.error('Error creating refund', {
        description: getErrorMessage(error),
      })
      if (cashDrawerSessionId !== null) {
        void refreshCashDrawerSession(currentDrawerQuery)
      }
    } finally {
      setIsSubmittingRefund(false)
    }
  }

  const handleModeChange = (nextMode: RefundQuoteMode) => {
    pendingRefundIdempotencyKeyRef.current = null
    setMode(nextMode)
    setSelectedRowIds([])
    setDraftAmounts({})
    setRestockQuantitiesByRowId({})
    setQuote(null)
    scheduleQuoteRefresh(nextMode)
  }

  const handleQuoteRowToggle = (row: RefundQuoteRow, checked: boolean) => {
    pendingRefundIdempotencyKeyRef.current = null
    const rowsToToggle = getQuoteRowsForSelectionToggle(row, quoteRowGroups, {
      isSelecting: checked,
      selectedRowIds,
    })
    const rowIdsToToggle = rowsToToggle.map((quoteRow) => Number(quoteRow.id))

    if (checked) {
      const nextSelectedRowIds = [...new Set([...selectedRowIds, ...rowIdsToToggle])]
      const nextDraftAmounts = { ...draftAmounts }

      rowsToToggle.forEach((quoteRow) => {
        const rowId = Number(quoteRow.id)
        nextDraftAmounts[rowId] ??= formatMoneyInputValue(quoteRow.max_amount ?? 0)
      })

      setSelectedRowIds(nextSelectedRowIds)
      setDraftAmounts(nextDraftAmounts)
      scheduleQuoteRefresh(
        'partial_line_item',
        buildQuoteRequestLineItems(nextSelectedRowIds, nextDraftAmounts)
      )

      return
    }

    const rowIdsToRemove = new Set(rowIdsToToggle)
    const nextSelectedRowIds = selectedRowIds.filter((rowId) => !rowIdsToRemove.has(Number(rowId)))
    const nextDraftAmounts = { ...draftAmounts }

    rowIdsToRemove.forEach((rowId) => {
      delete nextDraftAmounts[rowId]
    })

    setSelectedRowIds(nextSelectedRowIds)
    setDraftAmounts(nextDraftAmounts)
    setRestockQuantitiesByRowId((current) => {
      const next = { ...current }
      rowIdsToRemove.forEach((rowId) => {
        delete next[rowId]
      })

      return next
    })
    scheduleQuoteRefresh(
      'partial_line_item',
      buildQuoteRequestLineItems(nextSelectedRowIds, nextDraftAmounts)
    )
  }

  const handleQuoteRowAmountChange = (row: RefundQuoteRow, value: string) => {
    pendingRefundIdempotencyKeyRef.current = null
    const parsedAmount = parseCurrency(value)
    const maxAmount = Number(row?.max_amount ?? 0)
    const normalizedValue =
      value === '' || (parsedAmount >= 0 && parsedAmount <= maxAmount)
        ? value
        : formatMoneyInputValue(Math.min(Math.max(parsedAmount, 0), maxAmount))

    const nextDraftAmounts: DraftAmounts = {
      ...draftAmounts,
      [Number(row.id)]: normalizedValue,
    }

    setDraftAmounts(nextDraftAmounts)
    scheduleQuoteRefresh(
      'partial_line_item',
      buildQuoteRequestLineItems(selectedRowIds, nextDraftAmounts)
    )
  }

  const handleRestockQuantityChange = (row: RefundQuoteRow, value: number | string) => {
    pendingRefundIdempotencyKeyRef.current = null
    const maxQuantity = maxRestockQuantityForRow(row)
    const nextQuantity = Math.min(Math.max(Math.floor(Number(value ?? 0)), 0), maxQuantity)

    setRestockQuantitiesByRowId((current) => {
      const next = { ...current }

      if (nextQuantity > 0) {
        next[Number(row.id)] = nextQuantity
      } else {
        delete next[Number(row.id)]
      }

      return next
    })
  }

  const handleOpenRefundConfirm = () => {
    if (isRefundActionDisabled) {
      return
    }

    setIsRefundConfirmOpen(true)
  }

  // The confirmation stays open while its refund submits.
  const handleRefundConfirmOpenChange = (open: boolean) => {
    if (!isSubmittingRefund) {
      setIsRefundConfirmOpen(open)
    }
  }

  const customerEmailChoice = order?.purchaser?.email ? (
    <CustomerNotificationSwitch
      notifyCustomer={sendCustomerEmail}
      onNotifyCustomerChange={setSendCustomerEmail}
      disabled={isSubmittingRefund}
    />
  ) : (
    <Typography variant="description">
      No customer email address is saved. No email will be sent.
    </Typography>
  )

  useEffect(() => {
    return () => {
      clearPendingQuoteDebounce()
    }
  }, [])

  return (
    <OrderAsidePanelCard>
      <CardHeader>
        <CardTitle>Create Refund</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {refundRequestId ? (
          <RefundSettlementDetails
            query={settlementQuery}
            prepareCashConfirmation={prepareCashConfirmation}
          />
        ) : null}
        {!mode ? (
          <div className="grid gap-2">
            <Typography variant="supporting">
              Start with the type of refund you want to create.
            </Typography>
            <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
              <ActionSurface
                type="button"
                className="min-w-0"
                onClick={() => handleModeChange('full')}
              >
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                <span className="flex min-w-0 flex-col items-start gap-1">
                  <Typography variant="labelStrong" tone="inherit">
                    Full Refund
                  </Typography>
                  <Typography variant="caption">
                    Refund everything still left on the order.
                  </Typography>
                </span>
              </ActionSurface>
              <ActionSurface
                type="button"
                className="min-w-0"
                onClick={() => handleModeChange('partial_line_item')}
              >
                <PackageCheck className="mt-0.5 h-4 w-4 shrink-0" />
                <span className="flex min-w-0 flex-col items-start gap-1">
                  <Typography variant="labelStrong" tone="inherit">
                    Partial Refund
                  </Typography>
                  <Typography variant="caption">
                    Choose the line items and pre-tax amounts to refund.
                  </Typography>
                </span>
              </ActionSurface>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2">
              <Typography variant="label">
                {mode === 'full' ? 'Full Refund' : 'Partial Refund'}
              </Typography>
              <Button
                type="button"
                variant="soft"
                size="sm"
                className="shrink-0"
                onClick={resetDraftState}
              >
                Change refund type
              </Button>
            </div>

            <div className="grid gap-3">
              <div className="grid gap-1.5">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="refund-reason">Reason</Label>
                  {reason && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="micro"
                      onClick={() => setReason('')}
                    >
                      Clear
                    </Button>
                  )}
                </div>
                <Select
                  value={reason || ''}
                  onValueChange={(value) =>
                    setReason(
                      salesOrderOutcomeReasonOptions.find((option) => option.id === value)?.id ?? ''
                    )
                  }
                >
                  <SelectTrigger id="refund-reason" aria-label="Reason" className="w-full">
                    <SelectValue placeholder="Select a reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {salesOrderOutcomeReasonOptions.map((option) => (
                      <SelectItem key={option.id} value={String(option.id)}>
                        {option.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {outcomeReasonRequiresNote(reason) && (
                <div className="grid gap-1.5">
                  <Label htmlFor="refund-reason-note">Note</Label>
                  <Textarea
                    id="refund-reason-note"
                    rows={3}
                    maxLength={500}
                    value={reasonNote}
                    onChange={(event) => setReasonNote(event.target.value)}
                  />
                </div>
              )}
            </div>

            {mode === 'partial_line_item' && (
              <div className="grid gap-2">
                <div className="grid grid-cols-[1fr_100px] gap-0">
                  <Typography variant="label">Item</Typography>
                  <Typography variant="label" align="right">
                    Pre-tax amount
                  </Typography>
                </div>
                <Typography variant="caption">
                  Adjust the pre-tax refund amount for each selected row. Tax is added in the
                  summary below.
                </Typography>
                {isQuoteLoading && !quote ? (
                  <Skeleton className="h-24 w-full" />
                ) : quoteRows.length > 0 ? (
                  <ul className="space-y-2">
                    {quoteRowGroups.map((group) => (
                      <li key={group.key} className="space-y-2">
                        <ul className="space-y-2">
                          {group.rootRows.map((row) => (
                            <QuoteRow
                              key={row.id}
                              row={row}
                              isSelected={selectedRowIds.includes(Number(row.id))}
                              draftAmount={draftAmounts[Number(row.id)] ?? ''}
                              restockQuantity={Math.min(
                                Number(restockQuantitiesByRowId[Number(row.id)] ?? 0),
                                maxRestockQuantityForRow(row)
                              )}
                              maxRestockQuantity={maxRestockQuantityForRow(row)}
                              onToggle={handleQuoteRowToggle}
                              onAmountChange={handleQuoteRowAmountChange}
                              onRestockQuantityChange={handleRestockQuantityChange}
                            />
                          ))}
                        </ul>
                        {group.childRows.length > 0 ? (
                          <ul className="space-y-2">
                            {group.childRows.map((row) => (
                              <QuoteRow
                                key={row.id}
                                row={row}
                                relationship="child"
                                isSelected={selectedRowIds.includes(Number(row.id))}
                                draftAmount={draftAmounts[Number(row.id)] ?? ''}
                                restockQuantity={Math.min(
                                  Number(restockQuantitiesByRowId[Number(row.id)] ?? 0),
                                  maxRestockQuantityForRow(row)
                                )}
                                maxRestockQuantity={maxRestockQuantityForRow(row)}
                                onToggle={handleQuoteRowToggle}
                                onAmountChange={handleQuoteRowAmountChange}
                                onRestockQuantityChange={handleRestockQuantityChange}
                              />
                            ))}
                          </ul>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <Typography variant="supporting">
                    {quote?.ineligible_reason ?? 'There are no items left to refund.'}
                  </Typography>
                )}
                {hasQuoteDraftMismatch && !isQuoteLoading ? (
                  <Typography variant="caption" tone="warning">
                    Refresh the quote before submitting. The current draft no longer matches the
                    last successful quote.
                  </Typography>
                ) : null}
              </div>
            )}

            {mode === 'full' && (
              <FullRefundRestockRows
                rows={selectedRows}
                restockQuantitiesByRowId={restockQuantitiesByRowId}
                onRestockQuantityChange={handleRestockQuantityChange}
              />
            )}

            {restockCount > 0 && (
              <div className={cn(ORDER_ASIDE_SUBTLE_SURFACE_CLASS, 'space-y-1 px-4 py-3')}>
                <Typography variant="label">Inventory restock</Typography>
                <Typography variant="caption">
                  {`${restockCount} tracked item${restockCount === 1 ? '' : 's'} will be added back to inventory after this refund settles.`}
                </Typography>
              </div>
            )}

            {destinationPreviewLines.length > 0 && (
              <div className={cn(ORDER_ASIDE_RAISED_SURFACE_CLASS, 'space-y-2 px-4 py-3')}>
                <Typography variant="labelStrong">Refund destinations</Typography>
                <RefundDestinationList lines={destinationPreviewLines} />
                {hasRefundShortfall && (
                  <Typography variant="caption" tone="warning">
                    Only {formatCurrency(refundTotal - shortfallTotal)} is covered by collected
                    payments right now. Reduce this refund by {formatCurrency(shortfallTotal)} to
                    continue.
                  </Typography>
                )}
              </div>
            )}

            <div className={cn(ORDER_ASIDE_RAISED_SURFACE_CLASS, 'space-y-2 px-4 py-3')}>
              <Typography variant="labelStrong">Refund summary</Typography>
              <div className="grid gap-1">
                {summaryRows(
                  quote?.summary,
                  `${order?.florist_fee_label || 'Shop fee'} (Shop fee)`
                ).map(([label, amount]) =>
                  label === 'Total' ? (
                    <div
                      key={label}
                      className="mt-2 flex items-center justify-between gap-2 border-t border-border/60 pt-2"
                    >
                      <Typography variant="h4">Total</Typography>
                      {isQuoteLoading ? (
                        <Skeleton className="h-6 w-[72px]" />
                      ) : (
                        <Typography variant="h4">{formatCurrency(amount)}</Typography>
                      )}
                    </div>
                  ) : (
                    <div key={label} className="flex items-center justify-between gap-2">
                      <Typography variant="label" tone="muted" className="truncate">
                        {label}
                      </Typography>
                      {isQuoteLoading ? (
                        <Skeleton className="h-4 w-[50px]" />
                      ) : (
                        <Typography variant="label" tone="muted" className="shrink-0">
                          {formatCurrency(amount)}
                        </Typography>
                      )}
                    </div>
                  )
                )}
              </div>
              {parseCurrency(quote?.summary?.variable_service_fee_tax) > 0 && (
                <Typography variant="caption">
                  Tax includes {formatCurrency(quote?.summary.variable_service_fee_tax)} on the
                  Service Fee.
                </Typography>
              )}
            </div>

            <Button
              type="button"
              className="w-full"
              disabled={isRefundActionDisabled}
              onClick={handleOpenRefundConfirm}
            >
              {isSubmittingRefund ? 'Submitting refund...' : 'Refund'}
            </Button>

            {hasManualDestination ? (
              <CustomerHandoffDialog
                open={isRefundConfirmOpen}
                onOpenChange={handleRefundConfirmOpenChange}
                manualTotal={manualDestinationTotal}
                electronicTotal={cardDestinationTotal}
                confirmLabel="Confirm Handoff & Refund"
                onConfirm={() => void submitRefund()}
                isPending={isSubmittingRefund}
                confirmDisabled={isCashDrawerLoading || cashDrawerStatusUnavailable}
                till={
                  cashDrawerStatusUnavailable ? (
                    <CashDrawerStatusError
                      isRetrying={cashDrawerQueriesAreFetching(
                        cashDrawerStatusQuery,
                        workstationsQuery,
                        currentDrawerQuery
                      )}
                      onRetry={() =>
                        retryFailedCashDrawerQueries(
                          cashDrawerStatusQuery,
                          workstationsQuery,
                          currentDrawerQuery
                        )
                      }
                    />
                  ) : !isCashDrawerLoading && cashDrawerTrackingEnabled && canOperateCashDrawers ? (
                    <div className="space-y-2">
                      <Label>Register</Label>
                      {resolvedCashDrawer ? (
                        <Typography variant="supporting" className="block">
                          {getCashDrawerOptionLabel({
                            workstation: pairedWorkstation,
                            location: pairedWorkstation?.location,
                          })}
                        </Typography>
                      ) : (
                        <Typography variant="label" tone="destructive">
                          {pairingBlocker}
                        </Typography>
                      )}
                    </div>
                  ) : !isCashDrawerLoading && cashDrawerTrackingEnabled ? (
                    <Typography variant="label" tone="destructive">
                      You don&apos;t have permission to record a cash or check refund while cash
                      drawer tracking is on.
                    </Typography>
                  ) : null
                }
              >
                {customerEmailChoice}
              </CustomerHandoffDialog>
            ) : (
              <Dialog open={isRefundConfirmOpen} onOpenChange={handleRefundConfirmOpenChange}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Confirm Refund</DialogTitle>
                    <DialogDescription>
                      {mode === 'full'
                        ? 'Refund everything still left on this order?'
                        : 'Submit this partial refund?'}
                    </DialogDescription>
                  </DialogHeader>
                  {customerEmailChoice}
                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={isSubmittingRefund}
                      onClick={() => setIsRefundConfirmOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="button" disabled={isSubmittingRefund} onClick={submitRefund}>
                      Confirm Refund
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </>
        )}
      </CardContent>
    </OrderAsidePanelCard>
  )
}

export default PaidSalesOrderRefundForm
